import { FC, ReactNode, useCallback, useMemo, type KeyboardEvent } from 'react';
import { Dropdown, DropdownListItem, FInter, FlexBox, useBreakpoint } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { STATUSES } from '_competition/const';
import { OptionProps } from '_competition/pages/AllCompetitionsPage/types';
import { HoverItemText, HoverItemWithSvg, ProjectSearchBarRoot, SortSmallTextDiv } from 'shared/components/projectsSearchBar/style';
import { DropdownText } from 'shared/navbars/components/contextTabsItems';
import { ApprovedIcon } from 'shared/icons/ApprovedIcon';
import { LayoutConst } from 'core/modules';
import { SortIcon } from 'shared/icons';
import { AppTester, getCurrentAppName } from 'supporting';

const DropDownWrapper = styled(FlexBox)<{ isDark?: boolean }>`
  ${({ isDark, theme }) =>
    isDark &&
    css`
      .dropdown-show {
        .dropdown-list-item {
          background: ${(theme.competition?.langComponent?.notActive?.bg
            ? theme.competition.langComponent.notActive.bg
            : theme.navigationBar.login.dropDownBackground)} !important;
          & div {
            color: ${theme.mainDropdownStyles.color} !important;
          }
          .active {
            color: ${theme.mainDropdownStyles.colorActive} !important;
            svg path {
              fill: ${theme.mainDropdownStyles.colorActive} !important;
            }
          }
          &:hover {
            background: ${theme.navigationBar.login.dropDownHoverBackground} !important;
            & div {
              color: ${theme.mainDropdownStyles.color} !important;
              background: none;
            }
            .active {
              color: ${theme.mainDropdownStyles.colorActive} !important;
              svg path {
                fill: ${theme.mainDropdownStyles.colorActive} !important;
              }
            }
          }
        }
      }
    `}
`;

const SortBarShell: FC<{
  bare?: boolean;
  children: ReactNode;
  mobileInteractive?: boolean;
  onMobileClick?: () => void;
  onMobileKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
}> = ({ bare, children, mobileInteractive, onMobileClick, onMobileKeyDown }) => {
  const bareSortGap = 7;

  if (bare) {
    if (mobileInteractive) {
      return (
        <FlexBox
          align="center"
          gx={bareSortGap}
          role="button"
          tabIndex={0}
          onClick={onMobileClick}
          onKeyDown={onMobileKeyDown}
          style={{ cursor: 'pointer' }}
        >
          {children}
        </FlexBox>
      );
    }
    return (
      <FlexBox align="center" gx={bareSortGap}>
        {children}
      </FlexBox>
    );
  }

  if (mobileInteractive) {
    return (
      <ProjectSearchBarRoot
        align="center"
        role="button"
        tabIndex={0}
        onClick={onMobileClick}
        onKeyDown={onMobileKeyDown}
        style={{ cursor: 'pointer' }}
      >
        {children}
      </ProjectSearchBarRoot>
    );
  }

  return <ProjectSearchBarRoot align="center">{children}</ProjectSearchBarRoot>;
};

export const CompetitionDropdown: FC<{
    selectedSorting: STATUSES;
    onSortingChange: (sorting: OptionProps) => void;
    sortingMethods: OptionProps[];
    isDarkTheme?: boolean;
    bare?: boolean;
}> = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const defaultValue = props.sortingMethods[0];
    const isTabletOrBelow = useBreakpoint(LayoutConst.screen.l);
    const isDafes = AppTester.isGeoDafes();
    const isDesignContext =
      (getCurrentAppName() !== 'competition' &&
        getCurrentAppName() !== 'deziiign' &&
        !isDafes) ||
      props.isDarkTheme;
    const isDropdownDarkUi = isDesignContext || AppTester.isSuperPuper();
    const competitionPanelBg =
        theme.competition?.langComponent?.notActive?.bg ?? theme.navigationBar.login.dropdownContentBg;

    const cycleSort = useCallback(() => {
      const methods = props.sortingMethods;
      if (!methods?.length) return;
      const idx = methods.findIndex((m) => m.identifier === props.selectedSorting);
      const currentIdx = idx === -1 ? 0 : idx;
      const nextIdx = (currentIdx + 1) % methods.length;
      props.onSortingChange(methods[nextIdx]);
    }, [props.sortingMethods, props.selectedSorting, props.onSortingChange]);

    const onKeyDown = useCallback(
      (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          cycleSort();
        }
      },
      [cycleSort],
    );

    const completedOption =
      props.sortingMethods.find((i) => i?.identifier === STATUSES.Completed) || defaultValue;
    const openOption =
      props.sortingMethods.find((i) => i?.identifier === STATUSES.Open) || defaultValue;

    const desktopList = useMemo(
      () => [
        { id: STATUSES.Completed, option: completedOption },
        { id: STATUSES.Open, option: openOption },
      ],
      [completedOption, openOption],
    );

  if (!props.sortingMethods?.length) {
    return null;
  }

  if (isTabletOrBelow) {
    return (
      <SortBarShell
        bare={props.bare}
        mobileInteractive
        onMobileClick={cycleSort}
        onMobileKeyDown={onKeyDown}
      >
        <SortSmallTextDiv style={{ whiteSpace: 'pre' }} color={theme.sortByColor}>
          <SortIcon />
        </SortSmallTextDiv>
        <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
          <HoverItemText>
            {t('competition.statuses.' + STATUSES[props?.selectedSorting])}
          </HoverItemText>
        </HoverItemWithSvg>
      </SortBarShell>
    );
  }

  return (
    <SortBarShell bare={props.bare}>
      <SortSmallTextDiv style={{ whiteSpace: 'pre' }} color={theme.sortByColor}>
        {t('competition.show')}
      </SortSmallTextDiv>
      <DropDownWrapper isDark={props.isDarkTheme}>
        <Dropdown
          dark={isDropdownDarkUi}
          {...(isDropdownDarkUi
            ? { dropdownPopupBg: competitionPanelBg, dropdownContentBg: competitionPanelBg }
            : {})}
          offsetArrowX={38}
          orientation="right"
          hideArrow
          target={
            <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
              <HoverItemText>
                {t('competition.statuses.' + STATUSES[props?.selectedSorting])}
              </HoverItemText>
              <FInter.SmallText style={{ alignItems: 'center', display: 'flex' }}>
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                    fill={theme.tags.textColor}
                  />
                </svg>
              </FInter.SmallText>
            </HoverItemWithSvg>
          }
        >
          {desktopList.map(({ id, option }) => (
            <DropdownListItem key={id} onClick={() => props.onSortingChange(option)}>
              <FlexBox justify="space-between" align="center" style={{ width: '100%' }}>
                <DropdownText isActive={props.selectedSorting === id}>
                  {t('competition.statuses.' + STATUSES[id])}
                </DropdownText>
                <ApprovedIcon fill={theme.tags.textColor} display={props.selectedSorting === id} />
              </FlexBox>
            </DropdownListItem>
          ))}
        </Dropdown>
      </DropDownWrapper>
    </SortBarShell>
  );
};
import { FC, ReactNode, useCallback, useMemo, type KeyboardEvent } from 'react';
import { Dropdown, DropdownListItem, FInter, FlexBox, useBreakpoint } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { STATUSES } from '_competition/const';
import { OptionProps } from '_competition/pages/AllCompetitionsPage/types';
import { HoverItemText, HoverItemWithSvg, ProjectSearchBarRoot, SortSmallTextDiv } from 'shared/components/projectsSearchBar/style';
import { DropdownText } from 'shared/navbars/components/contextTabsItems';
import { ApprovedIcon } from 'shared/icons/ApprovedIcon';
import { LayoutConst } from 'core/modules';
import { SortIcon } from 'shared/icons';
import { AppTester, getCurrentAppName } from 'supporting';

const DropDownWrapper = styled(FlexBox)<{ isDark?: boolean }>`
  ${({ isDark, theme }) =>
    isDark &&
    css`
      .dropdown-show {
        .dropdown-list-item {
          background: ${(theme.competition?.langComponent?.notActive?.bg
            ? theme.competition.langComponent.notActive.bg
            : theme.navigationBar.login.dropDownBackground)} !important;
          & div {
            color: ${theme.mainDropdownStyles.color} !important;
          }
          .active {
            color: ${theme.mainDropdownStyles.colorActive} !important;
            svg path {
              fill: ${theme.mainDropdownStyles.colorActive} !important;
            }
          }
          &:hover {
            background: ${theme.navigationBar.login.dropDownHoverBackground} !important;
            & div {
              color: ${theme.mainDropdownStyles.color} !important;
              background: none;
            }
            .active {
              color: ${theme.mainDropdownStyles.colorActive} !important;
              svg path {
                fill: ${theme.mainDropdownStyles.colorActive} !important;
              }
            }
          }
        }
      }
    `}
`;

const SortBarShell: FC<{
  bare?: boolean;
  children: ReactNode;
  mobileInteractive?: boolean;
  onMobileClick?: () => void;
  onMobileKeyDown?: (e: KeyboardEvent<HTMLElement>) => void;
}> = ({ bare, children, mobileInteractive, onMobileClick, onMobileKeyDown }) => {
  const bareSortGap = 7;

  if (bare) {
    if (mobileInteractive) {
      return (
        <FlexBox
          align="center"
          gx={bareSortGap}
          role="button"
          tabIndex={0}
          onClick={onMobileClick}
          onKeyDown={onMobileKeyDown}
          style={{ cursor: 'pointer' }}
        >
          {children}
        </FlexBox>
      );
    }
    return (
      <FlexBox align="center" gx={bareSortGap}>
        {children}
      </FlexBox>
    );
  }

  if (mobileInteractive) {
    return (
      <ProjectSearchBarRoot
        align="center"
        role="button"
        tabIndex={0}
        onClick={onMobileClick}
        onKeyDown={onMobileKeyDown}
        style={{ cursor: 'pointer' }}
      >
        {children}
      </ProjectSearchBarRoot>
    );
  }

  return <ProjectSearchBarRoot align="center">{children}</ProjectSearchBarRoot>;
};

export const CompetitionDropdown: FC<{
    selectedSorting: STATUSES;
    onSortingChange: (sorting: OptionProps) => void;
    sortingMethods: OptionProps[];
    isDarkTheme?: boolean;
    bare?: boolean;
}> = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const defaultValue = props.sortingMethods[0];
    const isTabletOrBelow = useBreakpoint(LayoutConst.screen.l);
    const isDafes = AppTester.isGeoDafes();
    const isDesignContext =
      (getCurrentAppName() !== 'competition' &&
        getCurrentAppName() !== 'deziiign' &&
        !isDafes) ||
      props.isDarkTheme;
    const isDropdownDarkUi = isDesignContext || AppTester.isSuperPuper();
    const competitionPanelBg =
        theme.competition?.langComponent?.notActive?.bg ?? theme.navigationBar.login.dropdownContentBg;

    const cycleSort = useCallback(() => {
      const methods = props.sortingMethods;
      if (!methods?.length) return;
      const idx = methods.findIndex((m) => m.identifier === props.selectedSorting);
      const currentIdx = idx === -1 ? 0 : idx;
      const nextIdx = (currentIdx + 1) % methods.length;
      props.onSortingChange(methods[nextIdx]);
    }, [props.sortingMethods, props.selectedSorting, props.onSortingChange]);

    const onKeyDown = useCallback(
      (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          cycleSort();
        }
      },
      [cycleSort],
    );

    const completedOption =
      props.sortingMethods.find((i) => i?.identifier === STATUSES.Completed) || defaultValue;
    const openOption =
      props.sortingMethods.find((i) => i?.identifier === STATUSES.Open) || defaultValue;

    const desktopList = useMemo(
      () => [
        { id: STATUSES.Completed, option: completedOption },
        { id: STATUSES.Open, option: openOption },
      ],
      [completedOption, openOption],
    );

  if (!props.sortingMethods?.length) {
    return null;
  }

  if (isTabletOrBelow) {
    return (
      <SortBarShell
        bare={props.bare}
        mobileInteractive
        onMobileClick={cycleSort}
        onMobileKeyDown={onKeyDown}
      >
        <SortSmallTextDiv style={{ whiteSpace: 'pre' }} color={theme.sortByColor}>
          <SortIcon />
        </SortSmallTextDiv>
        <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
          <HoverItemText>
            {t('competition.statuses.' + STATUSES[props?.selectedSorting])}
          </HoverItemText>
        </HoverItemWithSvg>
      </SortBarShell>
    );
  }

  return (
    <SortBarShell bare={props.bare}>
      <SortSmallTextDiv style={{ whiteSpace: 'pre' }} color={theme.sortByColor}>
        {t('competition.show')}
      </SortSmallTextDiv>
      <DropDownWrapper isDark={props.isDarkTheme}>
        <Dropdown
          dark={isDropdownDarkUi}
          {...(isDropdownDarkUi
            ? { dropdownPopupBg: competitionPanelBg, dropdownContentBg: competitionPanelBg }
            : {})}
          offsetArrowX={38}
          orientation="right"
          hideArrow
          target={
            <HoverItemWithSvg color={theme.sortByColorSort} gx={8}>
              <HoverItemText>
                {t('competition.statuses.' + STATUSES[props?.selectedSorting])}
              </HoverItemText>
              <FInter.SmallText style={{ alignItems: 'center', display: 'flex' }}>
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 6.15801L1.25981 0.223468C0.971612 -0.0744925 0.504346 -0.0744886 0.216147 0.223475C-0.0720515 0.52144 -0.0720488 1.00453 0.216154 1.30249L6.47817 7.77653C6.76637 8.07449 7.23363 8.07449 7.52183 7.77653L13.7838 1.30249C14.072 1.00453 14.0721 0.52144 13.7839 0.223475C13.4957 -0.0744886 13.0284 -0.0744925 12.7402 0.223468L7 6.15801Z"
                    fill={theme.tags.textColor}
                  />
                </svg>
              </FInter.SmallText>
            </HoverItemWithSvg>
          }
        >
          {desktopList.map(({ id, option }) => (
            <DropdownListItem key={id} onClick={() => props.onSortingChange(option)}>
              <FlexBox justify="space-between" align="center" style={{ width: '100%' }}>
                <DropdownText isActive={props.selectedSorting === id}>
                  {t('competition.statuses.' + STATUSES[id])}
                </DropdownText>
                <ApprovedIcon fill={theme.tags.textColor} display={props.selectedSorting === id} />
              </FlexBox>
            </DropdownListItem>
          ))}
        </Dropdown>
      </DropDownWrapper>
    </SortBarShell>
  );
};
